import { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Pool, PoolType } from '../../types/pool';
import {
  ImportTeamsRequest,
  ImportPlayersRequest,
  ImportScheduleRequest,
  ImportResult,
} from '../../types/admin';

// Define tab configurations per pool type
type TabType = 'teams' | 'players' | 'drivers' | 'schedule';

interface TabConfig {
  tabs: TabType[];
  labels: Record<TabType, string>;
}

function getTabConfig(poolType: PoolType): TabConfig {
  switch (poolType) {
    case PoolType.FORMULA_1:
      return {
        tabs: ['teams', 'drivers', 'schedule'],
        labels: {
          teams: 'Teams',
          drivers: 'Drivers',
          players: 'Players',
          schedule: 'Race Schedule',
        },
      };
    case PoolType.OLYMPIC_HOCKEY:
    case PoolType.FOUR_NATIONS:
      return {
        tabs: ['teams', 'players', 'schedule'],
        labels: {
          teams: 'Countries',
          drivers: 'Drivers',
          players: 'Players',
          schedule: 'Games',
        },
      };
    case PoolType.NCAA_BASKETBALL_PICK6:
    case PoolType.NCAA_BASKETBALL_KNOCKOUT:
      return {
        tabs: ['teams', 'schedule'],
        labels: {
          teams: 'Teams (Bracket)',
          drivers: 'Drivers',
          players: 'Players',
          schedule: 'Games',
        },
      };
    case PoolType.NFL_NO_REPEAT:
      return {
        tabs: ['teams', 'players', 'schedule'],
        labels: {
          teams: 'Teams',
          drivers: 'Drivers',
          players: 'Players',
          schedule: 'Weekly Schedule (18 wks)',
        },
      };
    default:
      return {
        tabs: ['teams', 'players', 'schedule'],
        labels: {
          teams: 'Teams',
          drivers: 'Drivers',
          players: 'Players',
          schedule: 'Events',
        },
      };
  }
}

export function AdminImport() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [pool, setPool] = useState<Pool | null>(null);
  const [allPools, setAllPools] = useState<Pool[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('teams');
  const [jsonInput, setJsonInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Copy teams state
  const [selectedSourcePool, setSelectedSourcePool] = useState<string>('');
  const [copying, setCopying] = useState(false);

  // Auto roster import state (NFL pools — ESPN)
  const [autoImporting, setAutoImporting] = useState(false);
  const [autoImportMsg, setAutoImportMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Auto schedule import state (NFL No-Repeat — ESPN)
  const [autoSchedImporting, setAutoSchedImporting] = useState(false);
  const [autoSchedMsg, setAutoSchedMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [schedSeason, setSchedSeason] = useState('');

  const tabConfig = useMemo(() => {
    return getTabConfig(pool?.type || PoolType.CFP);
  }, [pool?.type]);

  useEffect(() => {
    if (poolId) {
      loadPool();
      loadAllPools();
    }
  }, [poolId]);

  // Reset to first available tab when pool loads
  useEffect(() => {
    if (pool && tabConfig.tabs.length > 0 && !tabConfig.tabs.includes(activeTab)) {
      setActiveTab(tabConfig.tabs[0]);
    }
  }, [pool, tabConfig]);

  async function loadPool() {
    try {
      const poolData = await api.getPool(poolId!);
      setPool(poolData);
    } catch (err) {
      console.error('Failed to load pool:', err);
    }
  }

  async function loadAllPools() {
    try {
      const pools = await api.getPools();
      setAllPools(pools.filter((p: Pool) => p.id !== poolId));
    } catch (err) {
      console.error('Failed to load pools:', err);
    }
  }

  async function handleAutoImportRosters() {
    if (!poolId) return;
    try {
      setAutoImporting(true);
      setAutoImportMsg(null);
      const token = await getAccessTokenSilently();
      const res = await api.autoImportRosters(poolId, token);
      const npCount = res.needsPosition?.length || 0;
      setAutoImportMsg({
        text: `Imported ${res.imported || 0} players, updated ${res.updated || 0} (${res.teamsProcessed || 0} teams)` +
          (npCount ? `. ${npCount} players need position assignment (see Players page).` : '') +
          (res.errors?.length ? ` Errors: ${res.errors.slice(0, 3).join('; ')}${res.errors.length > 3 ? '…' : ''}` : ''),
        type: res.errors?.length && !res.imported && !res.updated ? 'error' : 'success',
      });
    } catch (err) {
      setAutoImportMsg({ text: err instanceof Error ? err.message : 'Auto-import failed', type: 'error' });
    } finally {
      setAutoImporting(false);
    }
  }

  async function handleAutoImportSchedule() {
    if (!poolId) return;
    try {
      setAutoSchedImporting(true);
      setAutoSchedMsg(null);
      const token = await getAccessTokenSilently();
      const season = schedSeason.trim() ? Number(schedSeason.trim()) : undefined;
      const res = await api.autoImportNflSchedule(poolId, token, season);
      setAutoSchedMsg({
        text: `Season ${res.season}: ${res.created || 0} games created, ${res.updated || 0} updated (${res.weeksProcessed || 0}/18 weeks)` +
          (res.errors?.length ? `. Issues: ${res.errors.slice(0, 3).join('; ')}${res.errors.length > 3 ? ` (+${res.errors.length - 3} more)` : ''}` : ''),
        type: res.weeksProcessed > 0 ? 'success' : 'error',
      });
    } catch (err) {
      setAutoSchedMsg({ text: err instanceof Error ? err.message : 'Schedule auto-import failed', type: 'error' });
    } finally {
      setAutoSchedImporting(false);
    }
  }

  async function handleCopyTeams() {
    if (!poolId || !selectedSourcePool) return;

    try {
      setCopying(true);
      setError(null);
      setResult(null);

      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/admin/pools/${poolId}/copy-teams/${selectedSourcePool}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to copy teams');
      }

      const data = await response.json();
      setResult({
        total: data.total,
        created: data.created,
        updated: data.updated || 0,
        errors: data.errors || [],
        message: data.message,
      });
      setSelectedSourcePool('');
    } catch (err) {
      console.error('Copy teams failed:', err);
      setError(err instanceof Error ? err.message : 'Copy failed');
    } finally {
      setCopying(false);
    }
  }

  const handleImport = async () => {
    if (!poolId || !pool) return;

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const data = JSON.parse(jsonInput);
      const token = await getAccessTokenSilently();

      let importResult: ImportResult;

      // Handle F1-specific imports
      if (pool.type === PoolType.FORMULA_1) {
        if (activeTab === 'teams') {
          // F1 teams are just regular teams (constructors)
          importResult = await api.importTeams(poolId, data as ImportTeamsRequest, token);
        } else if (activeTab === 'drivers') {
          importResult = await api.importF1Drivers(poolId, data, token);
        } else if (activeTab === 'schedule') {
          importResult = await api.importF1Schedule(poolId, data, token);
        } else {
          throw new Error('Invalid tab');
        }
      } else {
        // Standard imports for other pool types
        switch (activeTab) {
          case 'teams':
            importResult = await api.importTeams(poolId, data as ImportTeamsRequest, token);
            break;
          case 'players':
            importResult = await api.importPlayers(poolId, data as ImportPlayersRequest, token);
            break;
          case 'schedule':
            if (pool.type === PoolType.NFL_NO_REPEAT) {
              importResult = await api.importNflSchedule(poolId, data as { games: any[] }, token);
            } else {
              importResult = await api.importSchedule(poolId, data as ImportScheduleRequest, token);
            }
            break;
          default:
            throw new Error('Invalid tab');
        }
      }

      setResult(importResult);
      if (importResult.created > 0 || importResult.updated > 0) {
        setJsonInput('');
      }
    } catch (err) {
      console.error('Import failed:', err);
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const getExampleJson = () => {
    const poolType = pool?.type || PoolType.CFP;

    // F1-specific examples
    if (poolType === PoolType.FORMULA_1) {
      return getF1Example(activeTab);
    }

    // Other pool types
    switch (activeTab) {
      case 'teams':
        return getTeamsExample(poolType);
      case 'players':
        return getPlayersExample(poolType);
      case 'schedule':
        return getScheduleExample(poolType);
      default:
        return '{}';
    }
  };

  // F1-specific examples
  const getF1Example = (tab: TabType) => {
    switch (tab) {
      case 'teams':
        return JSON.stringify({
          teams: [
            {
              name: "Red Bull Racing",
              shortName: "RBR",
              sport: "F1"
            },
            {
              name: "Ferrari",
              shortName: "FER",
              sport: "F1"
            },
            {
              name: "Mercedes-AMG Petronas",
              shortName: "MER",
              sport: "F1"
            },
            {
              name: "McLaren",
              shortName: "MCL",
              sport: "F1"
            },
            {
              name: "Aston Martin",
              shortName: "AMR",
              sport: "F1"
            },
            {
              name: "Alpine",
              shortName: "ALP",
              sport: "F1"
            },
            {
              name: "Williams",
              shortName: "WIL",
              sport: "F1"
            },
            {
              name: "RB",
              shortName: "RB",
              sport: "F1"
            },
            {
              name: "Kick Sauber",
              shortName: "SAU",
              sport: "F1"
            },
            {
              name: "Haas F1 Team",
              shortName: "HAA",
              sport: "F1"
            }
          ]
        }, null, 2);

      case 'drivers':
        return JSON.stringify({
          drivers: [
            {
              firstName: "Max",
              lastName: "Verstappen",
              driverNumber: 1,
              teamName: "Red Bull Racing",
              teamShortName: "RBR"
            },
            {
              firstName: "Lewis",
              lastName: "Hamilton",
              driverNumber: 44,
              teamName: "Ferrari",
              teamShortName: "FER"
            },
            {
              firstName: "Charles",
              lastName: "Leclerc",
              driverNumber: 16,
              teamName: "Ferrari",
              teamShortName: "FER"
            },
            {
              firstName: "Lando",
              lastName: "Norris",
              driverNumber: 4,
              teamName: "McLaren",
              teamShortName: "MCL"
            },
            {
              firstName: "Oscar",
              lastName: "Piastri",
              driverNumber: 81,
              teamName: "McLaren",
              teamShortName: "MCL"
            }
          ]
        }, null, 2);

      case 'schedule':
        return JSON.stringify({
          races: [
            {
              raceNumber: 1,
              name: "Bahrain Grand Prix",
              scheduledAt: "2026-03-07T15:00:00Z",
              location: "Sakhir",
              isSprintRace: false
            },
            {
              raceNumber: 2,
              name: "Saudi Arabian Grand Prix",
              scheduledAt: "2026-03-14T17:00:00Z",
              location: "Jeddah",
              isSprintRace: false
            },
            {
              raceNumber: 3,
              name: "Australian Grand Prix",
              scheduledAt: "2026-03-21T06:00:00Z",
              location: "Melbourne",
              isSprintRace: false
            },
            {
              raceNumber: 4,
              name: "Japanese Grand Prix",
              scheduledAt: "2026-04-04T06:00:00Z",
              location: "Suzuka",
              isSprintRace: false
            },
            {
              raceNumber: 5,
              name: "Chinese Grand Prix",
              scheduledAt: "2026-04-18T08:00:00Z",
              location: "Shanghai",
              isSprintRace: true
            }
          ]
        }, null, 2);

      default:
        return '{}';
    }
  };

  const getTeamsExample = (poolType: PoolType) => {
    if (poolType === PoolType.NCAA_BASKETBALL_PICK6 || poolType === PoolType.NCAA_BASKETBALL_KNOCKOUT) {
      return JSON.stringify({
        teams: [
          {
            name: "Duke Blue Devils",
            shortName: "Duke",
            sport: "NCAAB",
            seed: 1,
            metadata: { region: "East" }
          },
          {
            name: "Kansas Jayhawks",
            shortName: "Kansas",
            sport: "NCAAB",
            seed: 1,
            metadata: { region: "Midwest" }
          },
          {
            name: "Gonzaga Bulldogs",
            shortName: "Gonzaga",
            sport: "NCAAB",
            seed: 2,
            metadata: { region: "West" }
          },
          {
            name: "Houston Cougars",
            shortName: "Houston",
            sport: "NCAAB",
            seed: 1,
            metadata: { region: "South" }
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NFL_PLAYOFF) {
      return JSON.stringify({
        teams: [
          {
            name: "Kansas City Chiefs",
            shortName: "Kansas City",
            sport: "NFL",
            seed: 1
          },
          {
            name: "Buffalo Bills",
            shortName: "Buffalo",
            sport: "NFL",
            seed: 2
          },
          {
            name: "Baltimore Ravens",
            shortName: "Baltimore",
            sport: "NFL",
            seed: 3
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NHL_PLAYOFF) {
      return JSON.stringify({
        teams: [
          {
            name: "Florida Panthers",
            shortName: "Florida",
            sport: "NHL",
            seed: 1
          },
          {
            name: "Carolina Hurricanes",
            shortName: "Carolina",
            sport: "NHL",
            seed: 2
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NBA_PLAYOFF) {
      return JSON.stringify({
        teams: [
          {
            name: "Boston Celtics",
            shortName: "Boston",
            sport: "NBA",
            seed: 1
          },
          {
            name: "Denver Nuggets",
            shortName: "Denver",
            sport: "NBA",
            seed: 2
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.OLYMPIC_HOCKEY) {
      return JSON.stringify({
        teams: [
          {
            name: "Canada",
            shortName: "CAN",
            sport: "Hockey"
          },
          {
            name: "United States",
            shortName: "USA",
            sport: "Hockey"
          },
          {
            name: "Sweden",
            shortName: "SWE",
            sport: "Hockey"
          },
          {
            name: "Finland",
            shortName: "FIN",
            sport: "Hockey"
          },
          {
            name: "Russia",
            shortName: "RUS",
            sport: "Hockey"
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.FOUR_NATIONS) {
      return JSON.stringify({
        teams: [
          {
            name: "Canada",
            shortName: "CAN",
            sport: "Hockey"
          },
          {
            name: "United States",
            shortName: "USA",
            sport: "Hockey"
          },
          {
            name: "Sweden",
            shortName: "SWE",
            sport: "Hockey"
          },
          {
            name: "Finland",
            shortName: "FIN",
            sport: "Hockey"
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.WORLD_CUP_PICK6 || poolType === PoolType.WORLD_CUP_SURVIVOR) {
      // World Cup pools: 12 FIFA-ranking seed tiers (1–12), ISO country codes
      // for flag rendering (lowercase alpha-2; gb-eng / gb-wls supported).
      return JSON.stringify({
        teams: [
          {
            name: "Argentina",
            shortName: "ARG",
            sport: "Soccer",
            seed: 1,
            metadata: { isoCode: "ar" }
          },
          {
            name: "Brazil",
            shortName: "BRA",
            sport: "Soccer",
            seed: 2,
            metadata: { isoCode: "br" }
          },
          {
            name: "England",
            shortName: "ENG",
            sport: "Soccer",
            seed: 1,
            metadata: { isoCode: "gb-eng" }
          }
        ]
      }, null, 2);
    } else {
      // CFP default
      return JSON.stringify({
        teams: [
          {
            name: "Oregon Ducks",
            shortName: "Oregon",
            sport: "CFB",
            seed: 1
          },
          {
            name: "Georgia Bulldogs",
            shortName: "Georgia",
            sport: "CFB",
            seed: 2
          }
        ]
      }, null, 2);
    }
  };

  const getPlayersExample = (poolType: PoolType) => {
    if (poolType === PoolType.NFL_PLAYOFF) {
      return JSON.stringify({
        players: [
          {
            firstName: "Patrick",
            lastName: "Mahomes",
            position: "QB",
            teamName: "Kansas City"
          },
          {
            firstName: "Tyreek",
            lastName: "Hill",
            position: "WR",
            teamName: "Miami"
          },
          {
            firstName: "Christian",
            lastName: "McCaffrey",
            position: "RB",
            teamName: "San Francisco"
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NHL_PLAYOFF) {
      return JSON.stringify({
        players: [
          {
            firstName: "Connor",
            lastName: "McDavid",
            position: "C",
            teamName: "Edmonton"
          },
          {
            firstName: "Auston",
            lastName: "Matthews",
            position: "C",
            teamName: "Toronto"
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NBA_PLAYOFF) {
      return JSON.stringify({
        players: [
          {
            firstName: "Jayson",
            lastName: "Tatum",
            position: "SF",
            teamName: "Boston"
          },
          {
            firstName: "Nikola",
            lastName: "Jokic",
            position: "C",
            teamName: "Denver"
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.OLYMPIC_HOCKEY || poolType === PoolType.FOUR_NATIONS) {
      return JSON.stringify({
        players: [
          {
            firstName: "Connor",
            lastName: "McDavid",
            position: "F",
            teamName: "CAN"
          },
          {
            firstName: "Auston",
            lastName: "Matthews",
            position: "F",
            teamName: "USA"
          },
          {
            firstName: "William",
            lastName: "Nylander",
            position: "F",
            teamName: "SWE"
          },
          {
            firstName: "Cale",
            lastName: "Makar",
            position: "D",
            teamName: "CAN"
          },
          {
            firstName: "Connor",
            lastName: "Hellebuyck",
            position: "G",
            teamName: "USA"
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.CFP) {
      return JSON.stringify({
        players: [
          {
            firstName: "Dillon",
            lastName: "Gabriel",
            position: "QB",
            teamName: "Oregon"
          },
          {
            firstName: "Jordan",
            lastName: "James",
            position: "RB",
            teamName: "Oregon"
          }
        ]
      }, null, 2);
    } else {
      // Default
      return JSON.stringify({
        players: [
          {
            firstName: "Player",
            lastName: "One",
            position: "POS",
            teamName: "Team Name"
          }
        ]
      }, null, 2);
    }
  };

  const getScheduleExample = (poolType: PoolType) => {
    if (poolType === PoolType.NFL_NO_REPEAT) {
      return JSON.stringify({
        games: [
          {
            week: 1,
            scheduledAt: "2026-09-13T17:00:00-04:00",
            team1Name: "Kansas City Chiefs",
            team2Name: "Baltimore Ravens",
            location: "GEHA Field at Arrowhead Stadium"
          },
          {
            week: 14,
            scheduledAt: "2026-12-13T18:00:00-05:00",
            team1Name: "Buffalo Bills",
            team2Name: "Miami Dolphins",
            location: "Highmark Stadium"
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NFL_PLAYOFF) {
      return JSON.stringify({
        events: [
          {
            name: "Wild Card - Game 1",
            scheduledAt: "2026-01-13T16:30:00Z",
            team1Name: "Kansas City",
            team2Name: "Pittsburgh",
            location: "Arrowhead Stadium",
            round: "Wild Card",
            isFinal: false
          },
          {
            name: "Super Bowl LX",
            scheduledAt: "2026-02-08T23:30:00Z",
            location: "Levi's Stadium",
            round: "Super Bowl",
            isFinal: true
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NHL_PLAYOFF) {
      return JSON.stringify({
        events: [
          {
            name: "First Round - Game 1",
            scheduledAt: "2026-04-20T19:00:00Z",
            team1Name: "Florida",
            team2Name: "Tampa Bay",
            location: "FLA Live Arena",
            round: "First Round",
            isFinal: false
          },
          {
            name: "Stanley Cup Final - Game 7",
            scheduledAt: "2026-06-20T20:00:00Z",
            location: "TBD",
            round: "Stanley Cup Final",
            isFinal: true
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NBA_PLAYOFF) {
      return JSON.stringify({
        events: [
          {
            name: "First Round - Game 1",
            scheduledAt: "2026-04-19T19:30:00Z",
            team1Name: "Boston",
            team2Name: "Miami",
            location: "TD Garden",
            round: "First Round",
            isFinal: false
          },
          {
            name: "NBA Finals - Game 7",
            scheduledAt: "2026-06-22T20:00:00Z",
            location: "TBD",
            round: "NBA Finals",
            isFinal: true
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.OLYMPIC_HOCKEY) {
      return JSON.stringify({
        events: [
          {
            name: "Group A - Canada vs USA",
            scheduledAt: "2026-02-12T19:00:00Z",
            team1Name: "CAN",
            team2Name: "USA",
            location: "Milano",
            round: "Group Stage",
            isFinal: false
          },
          {
            name: "Gold Medal Game",
            scheduledAt: "2026-02-22T13:00:00Z",
            location: "Milano",
            round: "Gold Medal",
            isFinal: true
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.FOUR_NATIONS) {
      return JSON.stringify({
        events: [
          {
            name: "Canada vs USA",
            scheduledAt: "2025-02-12T20:00:00Z",
            team1Name: "CAN",
            team2Name: "USA",
            location: "Montreal",
            round: "Round Robin",
            isFinal: false
          },
          {
            name: "Championship Game",
            scheduledAt: "2025-02-20T20:00:00Z",
            location: "Boston",
            round: "Final",
            isFinal: true
          }
        ]
      }, null, 2);
    } else if (poolType === PoolType.NCAA_BASKETBALL_PICK6 || poolType === PoolType.NCAA_BASKETBALL_KNOCKOUT) {
      return JSON.stringify({
        events: [
          {
            name: "First Round - East #1 vs East #16",
            scheduledAt: "2026-03-19T12:00:00Z",
            team1Name: "Duke",
            team2Name: "Norfolk State",
            location: "TBD",
            round: "First Round",
            isFinal: false
          },
          {
            name: "National Championship",
            scheduledAt: "2026-04-06T21:00:00Z",
            location: "Lucas Oil Stadium",
            round: "Championship",
            isFinal: true
          }
        ]
      }, null, 2);
    } else {
      // CFP default
      return JSON.stringify({
        events: [
          {
            name: "CFP Quarterfinal #1",
            scheduledAt: "2025-12-31T20:00:00Z",
            team1Name: "Oregon",
            team2Name: "Ohio State",
            location: "Rose Bowl",
            round: "Quarterfinals",
            isFinal: false
          },
          {
            name: "CFP Championship",
            scheduledAt: "2026-01-19T20:00:00Z",
            location: "Mercedes-Benz Stadium",
            round: "Championship",
            isFinal: true
          }
        ]
      }, null, 2);
    }
  };

  const getPoolTypeLabel = (poolType: PoolType): string => {
    switch (poolType) {
      case PoolType.FORMULA_1: return 'Formula 1';
      case PoolType.CFP: return 'College Football Playoff';
      case PoolType.NFL_PLAYOFF: return 'NFL Playoffs';
      case PoolType.NHL_PLAYOFF: return 'NHL Playoffs';
      case PoolType.NBA_PLAYOFF: return 'NBA Playoffs';
      case PoolType.NCAA_BASKETBALL_PICK6: return 'March Madness Pick 6';
      case PoolType.NCAA_BASKETBALL_KNOCKOUT: return 'March Madness Knockout';
      case PoolType.OLYMPIC_HOCKEY: return 'Olympic Hockey';
      case PoolType.FOUR_NATIONS: return '4 Nations Face-Off';
      default: return poolType;
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/admin"
          className="text-sm text-blue-600 hover:text-blue-700 mb-2 inline-block"
        >
          ← Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Import Data</h1>
        {pool && (
          <p className="text-gray-600 mt-2">
            {pool.name} • <span className="text-blue-600">{getPoolTypeLabel(pool.type)}</span>
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <div className="flex">
            {tabConfig.tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setJsonInput('');
                  setResult(null);
                  setError(null);
                }}
                className={`px-6 py-3 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tabConfig.labels[tab]}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Copy Teams from Another Pool - Teams tab only */}
          {activeTab === 'teams' && allPools.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">
                📋 Copy {pool?.type === PoolType.FORMULA_1 ? 'Teams' : 'Teams'} from Another Pool
              </h3>
              <p className="text-xs text-blue-700 mb-3">
                Quickly copy all teams (with seeds and regions) from an existing pool.
              </p>
              <div className="flex gap-3">
                <select
                  value={selectedSourcePool}
                  onChange={(e) => setSelectedSourcePool(e.target.value)}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">Select a pool to copy from...</option>
                  {allPools.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.type})
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleCopyTeams}
                  disabled={copying || !selectedSourcePool}
                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {copying ? 'Copying...' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Divider for teams tab */}
          {activeTab === 'teams' && allPools.length > 0 && (
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or import manually</span>
              </div>
            </div>
          )}

          {/* NFL: one-click roster auto-import from ESPN (players tab) */}
          {activeTab === 'players' &&
            (pool?.type === PoolType.NFL_NO_REPEAT || pool?.type === PoolType.NFL_PLAYOFF) && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-emerald-900 mb-2">
                ⚡ Auto-Import Rosters (ESPN)
              </h3>
              <p className="text-xs text-emerald-700 mb-3">
                Pulls every pool team's current roster from ESPN — QB/RB/WR/TE with external IDs for
                automatic stat matching. Import teams first.
              </p>
              <button
                onClick={handleAutoImportRosters}
                disabled={autoImporting}
                className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
              >
                {autoImporting ? 'Importing rosters…' : 'Auto-Import All Rosters'}
              </button>
              {autoImportMsg && (
                <p className={`mt-3 text-xs ${autoImportMsg.type === 'success' ? 'text-emerald-800' : 'text-red-700'}`}>
                  {autoImportMsg.text}
                </p>
              )}
              <div className="relative mt-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-emerald-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-emerald-50 text-emerald-600">or paste JSON manually below</span>
                </div>
              </div>
            </div>
          )}

          {/* NFL No-Repeat: one-click 18-week schedule auto-import from ESPN (schedule tab) */}
          {activeTab === 'schedule' && pool?.type === PoolType.NFL_NO_REPEAT && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-emerald-900 mb-2">
                ⚡ Auto-Import 18-Week Schedule (ESPN)
              </h3>
              <p className="text-xs text-emerald-700 mb-3">
                Pulls all regular-season games (Weeks 1–18) with UTC kickoff times, venues, and
                game IDs for exact stat matching. Import teams first. Re-running updates existing games
                (useful for flexed kickoff times).
              </p>
              <div className="flex gap-3 items-center">
                <input
                  type="number"
                  value={schedSeason}
                  onChange={(e) => setSchedSeason(e.target.value)}
                  placeholder={`Season (default ${new Date().getMonth() >= 2 ? new Date().getFullYear() : new Date().getFullYear() - 1})`}
                  className="w-56 px-3 py-2 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
                <button
                  onClick={handleAutoImportSchedule}
                  disabled={autoSchedImporting}
                  className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {autoSchedImporting ? 'Importing schedule…' : 'Auto-Import Schedule'}
                </button>
              </div>
              {autoSchedMsg && (
                <p className={`mt-3 text-xs ${autoSchedMsg.type === 'success' ? 'text-emerald-800' : 'text-red-700'}`}>
                  {autoSchedMsg.text}
                </p>
              )}
              <div className="relative mt-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-emerald-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-emerald-50 text-emerald-600">or paste JSON manually below</span>
                </div>
              </div>
            </div>
          )}

          {/* Example */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Expected JSON Format
              </label>
              <button
                onClick={() => setJsonInput(getExampleJson())}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Use Example
              </button>
            </div>
            <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto max-h-64">
              <code>{getExampleJson()}</code>
            </pre>
          </div>

          {/* JSON Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste JSON Data
            </label>
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder={`Paste your ${tabConfig.labels[activeTab].toLowerCase()} JSON here...`}
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            />
          </div>

          {/* Import Button */}
          <button
            onClick={handleImport}
            disabled={loading || !jsonInput.trim()}
            className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Importing...
              </div>
            ) : (
              `Import ${tabConfig.labels[activeTab]}`
            )}
          </button>

          {/* Error */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800">Import Failed</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          )}

          {/* Success Result */}
          {result && (
            <div className={`mt-4 border rounded-lg p-4 ${
              result.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
            }`}>
              <p className={`text-sm font-medium ${
                result.errors.length > 0 ? 'text-yellow-800' : 'text-green-800'
              }`}>
                {result.message}
              </p>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div>
                  <div className="text-xs text-gray-600">Total</div>
                  <div className="text-lg font-semibold text-gray-900">{result.total}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Created</div>
                  <div className="text-lg font-semibold text-green-600">{result.created}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Updated</div>
                  <div className="text-lg font-semibold text-blue-600">{result.updated}</div>
                </div>
              </div>
              {result.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-yellow-800 mb-1">Errors:</p>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    {result.errors.map((err: string, i: number) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
